import type { ChantierCategorie } from "@/lib/schemas/chantier";

export const COMPANY = {
  name: "Symbiosis Paysage Services",
  shortName: "Symbiosis",
  tagline: "Paysagiste en Côte d'Émeraude depuis 2021.",
  phoneDisplay: "02 96 89 77 73",
  phoneHref: "+33296897773",
  email: "symbiosis.paysage.services@gmail.com",
  address: {
    street: "1 Lieu-dit Beauvais",
    postalCode: "22690",
    city: "Pleudihen-sur-Rance",
    region: "Côtes d'Armor",
    country: "FR",
  },
  hoursDisplay: "Lundi au vendredi, 8h à 18h",
  foundedYear: 2021,
  legal: {
    form: "SARL",
    capital: "15 000 EUR",
    siren: "908 169 097",
    siret: "908 169 097 00023",
    rcs: "Saint-Malo",
  },
  accreditation: {
    label: "Membre UNEP",
    full: "Membre de l'UNEP - Union Nationale des Entreprises du Paysage",
    since: 2021,
    url: "https://www.unep-fr.org",
  },
  sap: {
    label: "Agréée Services à la Personne",
    short: "SAP",
    agrementNumber: "908169097",
    declarationDate: "21/12/2021",
  },
} as const;

export const UNEP_LOGO = "/images/unep-logo.png";
// Variante texte blanc, pour fonds sombres/colorés (ex. bloc vert "Le label UNEP").
export const UNEP_LOGO_DARK = "/images/unep-logo-dark-bg.png";

export const ZONES: readonly string[] = [
  "Pleudihen-sur-Rance",
  "La Vicomte-sur-Rance",
  "Lanvallay",
  "Dinan",
  "Saint-Malo",
  "Dinard",
  "Dol-de-Bretagne",
] as const;

/**
 * Communes prioritaires pour le SEO local (sous-titre hero).
 * Dérivé de ZONES en écartant les petites communes (sous-titre plus lisible),
 * ce qui garantit que chaque ville reste un sous-ensemble de l'`areaServed`
 * du JSON-LD (cf. lib/seo.ts) sans risque de divergence.
 * Ordre obtenu : Pleudihen-sur-Rance, Dinan, Saint-Malo, Dinard, Dol-de-Bretagne.
 */
const ZONES_SECONDARY: readonly string[] = ["La Vicomte-sur-Rance", "Lanvallay"];
export const ZONES_PRIMARY: readonly string[] = ZONES.filter(
  (zone) => !ZONES_SECONDARY.includes(zone),
);

export type MembreEquipe = {
  id: "tristan";
  prenom: string;
  /** Nom affiché sous la signature, et repris comme `founder` du JSON-LD. */
  nomComplet: string;
  role: string;
  specialite: string;
  /**
   * Récit du parcours, un paragraphe par entrée, affiché uniquement s'il est
   * fourni : à null, la section se tient sans lui et aucun texte de
   * remplissage n'est publié.
   */
  parcours: readonly string[] | null;
  /** Citation/philosophie, même règle que `parcours`. */
  citation: string | null;
  /**
   * Pas de champ photo : Tristan ne souhaite pas de portrait, Marion non plus.
   * La signature manuscrite tient lieu de présence humaine sur la page.
   */
  signature: string | null;
};

// Marion (co-gérante) ne souhaite pas apparaître publiquement : pas de portrait
// ni de mention nominative côté front. Son rôle (relation client + devis) reste
// porté par l'entreprise via la section "Comment on travaille".
export const EQUIPE: readonly MembreEquipe[] = [
  {
    id: "tristan",
    prenom: "Tristan",
    nomComplet: "Tristan Letaconnoux",
    role: "Gérant",
    specialite: "Direction des chantiers",
    // Texte fourni par Marion (« Parcours Tristan », révision du 09/10/2026), à la
    // première personne. Reproduit tel quel, hormis l'accord « telles que »
    // (tailles) et la typographie alignée sur le site : apostrophes droites,
    // espaces insécables devant « : » et dans les guillemets.
    // 2021 est l'année de sa dernière fonction salariée, pas une date de
    // création d'entreprise : ne pas la reprendre ailleurs à ce titre.
    parcours: [
      "Fort de vingt ans d'expérience dans le paysage, j'ai débuté ma carrière par la voie de l'apprentissage. J'ai évolué au sein de diverses structures : entreprises privées, associations, secteur public, entreprise pour l'insertion. J'ai occupé des postes à niveaux de responsabilité progressifs pour lesquels j'ai travaillé à la fois sur des chantiers d'aménagements et d'entretien paysagers.",
      "Nourri par une solide expérience du terrain, j'ai décidé d'opérer une transition du salariat vers l'entrepreneuriat après ma dernière fonction de responsable de secteur en 2021.",
      "Ainsi, j'ai mobilisé les compétences acquises tout au long de mon parcours, qu'elles soient humaines ou techniques, cela me permettant aujourd'hui d'agir avec mon équipe de façon professionnelle et efficace.",
      "Je saisis chaque occasion d'enrichir ma connaissance des végétaux et je cultive un intérêt pour les tailles spécifiques telles que la taille en nuage, la taille fruitière, la taille « naturelle » des arbustes ou encore l'art topiaire. J'ai à cœur de partager ma passion pour les plantes et offrir un service personnalisé de qualité.",
    ],
    citation: null,
    signature: "/images/signature-tristan.png",
  },
] as const;

export type JalonHistoire = {
  /**
   * Repère de la frise. Les événements sont groupés dessous plutôt que listés à
   * plat : deux choses se sont passées en 2021, et la liste plate affichait
   * donc deux repères « 2021 » identiques l'un sous l'autre.
   * Ce n'est pas toujours une année, le dernier repère clôt la frise au présent.
   */
  repere: string;
  evenements: readonly { titre: string; lieu?: string }[];
};

export const HISTOIRE_TIMELINE: readonly JalonHistoire[] = [
  {
    repere: "2021",
    evenements: [
      { titre: "Création de Symbiosis", lieu: "La Vicomté-sur-Rance" },
      { titre: "Adhésion à l'UNEP" },
    ],
  },
  {
    repere: "2024",
    evenements: [{ titre: "Installation à Pleudihen-sur-Rance" }],
  },
  // Clôt la frise au présent : sans ce repère elle s'arrêtait en 2024, ce qui
  // laissait entendre que rien ne s'est passé depuis.
  // Porte les métiers et non la zone : le paragraphe qui jouxte la frise dit
  // déjà « nous rayonnons aujourd'hui sur toute la Rance et la Côte
  // d'Émeraude », la répéter ici ne servait à rien.
  {
    repere: "Aujourd'hui",
    evenements: [{ titre: "Entretien courant, tailles spécifiques et aménagements extérieurs" }],
  },
] as const;

export type Croquis = {
  id: string;
  src: string;
  /** Dimensions natives du webp, requises par next/image hors mode `fill`. */
  width: number;
  height: number;
  /** Légende affichée sous le croquis. */
  legende: string;
  /** Alt descriptif : ce que montre le dessin, pas son rôle éditorial. */
  alt: string;
};

// Croquis d'étude dessinés à la main par Tristan, photographiés tels quels
// (papier, crayon de couleur, tampon de l'entreprise). Volontairement non
// recadrés : le grain du papier et le tampon font partie de la preuve
// artisanale. Les noms de clients sont masqués à la source sur les originaux.
// Jeu remplacé en octobre 2026 par les croquis envoyés par Marion. Les plans
// passent en premier, comme dans le texte de la section (plan de masse, puis
// perspectives). Le dernier, en portrait, montre la photo de l'existant
// au-dessus de sa perspective.
export const CROQUIS: readonly Croquis[] = [
  {
    id: "plan-masse-saint-malo",
    src: "/croquis/plan-masse-saint-malo.webp",
    width: 1485,
    height: 1080,
    legende: "Plan de masse mis en couleur à Saint-Malo, échelle 1/50",
    alt: "Plan de masse d'un jardin à Saint-Malo dessiné à la main et mis en couleur au crayon : deux parkings, une terrasse, un arbre au centre et des massifs en courbe jusqu'au chêne vert, échelle 1/50",
  },
  {
    id: "plan-technique-saint-suliac",
    src: "/croquis/plan-technique-saint-suliac.webp",
    width: 1455,
    height: 1080,
    legende: "Plan technique coté à Saint-Suliac, relevé sur site",
    alt: "Plan technique d'un jardin à Saint-Suliac dessiné à l'encre : implantation de la maison, haie, pelouse en courbe, massif de rocaille et arbres, avec cotes relevées en bas du plan",
  },
  {
    id: "perspective-pelouse-saint-suliac",
    src: "/croquis/perspective-pelouse-saint-suliac.webp",
    width: 1514,
    height: 1080,
    legende: "Perspective à Saint-Suliac : la pelouse et ses massifs de bordure",
    alt: "Perspective au crayon de couleur montrant une grande pelouse ovale, un arbre isolé au centre et des massifs de graminées et d'arbustes en bordure",
  },
  {
    id: "perspective-palissage-saint-malo",
    src: "/croquis/perspective-palissage-saint-malo.webp",
    width: 1620,
    height: 1080,
    legende: "Perspective à Saint-Malo : plantations et palissage",
    alt: "Perspective au crayon de couleur d'une cour à Saint-Malo : terrasse en bois, pelouse avec pas japonais, arbres palissés contre les murs et massifs de plantes basses en bordure",
  },
  {
    id: "perspective-terrasse-pleudihen",
    src: "/croquis/perspective-terrasse-pleudihen.webp",
    width: 1440,
    height: 1080,
    legende: "Perspective à Pleudihen-sur-Rance : la terrasse bois et son massif",
    alt: "Perspective au crayon de couleur d'une maison en pierre à Pleudihen-sur-Rance : terrasse en lames de bois bordée d'un massif en courbe, arbuste en boule et graminées devant une clôture",
  },
  {
    id: "perspective-pas-japonais-pleudihen",
    src: "/croquis/perspective-pas-japonais-pleudihen.webp",
    width: 1440,
    height: 1080,
    legende: "Perspective à Pleudihen-sur-Rance : le pas japonais et ses massifs",
    alt: "Perspective au crayon de couleur d'un jardin à Pleudihen-sur-Rance : pas japonais en dalles, massifs de vivaces et de graminées en courbe, arbre taillé en nuage à l'arrière-plan",
  },
  {
    id: "avant-apres-pergola",
    src: "/croquis/avant-apres-pergola.webp",
    width: 810,
    height: 1080,
    legende: "Avant / après : la photo du jardin existant et sa perspective",
    alt: "Photo d'une petite cour enherbée avec une table de jardin, posée au-dessus de sa perspective dessinée au crayon de couleur : pergola en bois végétalisée, terrasse et jardinières",
  },
] as const;

export type Prestation = {
  id: string;
  icon: "plant" | "leaf" | "tree" | "bulb" | "spray";
  title: string;
  description: string;
  bullets: readonly string[];
};

// Sous-titre partage par la section services de l'accueil (services-preview)
// et la page /services (prestations) : source unique pour eviter la divergence.
export const PRESTATIONS_SUBTITLE =
  "Entretien courant, tailles spécifiques et aménagements extérieurs.";

// Textes revus par Marion (revision du 09/10/2026), reproduits a la lettre.
export const PRESTATIONS: readonly Prestation[] = [
  {
    id: "entretien",
    icon: "leaf",
    title: "Entretien de jardin",
    description:
      "Demande ponctuelle ou contrat à l'année, votre jardin reste impeccable en toute saison.",
    bullets: [
      "Entretien classique (tonte, taille, désherbage)",
      "Entretien des spécifiques (scarification, terreautage)",
      "Pratiques raisonnées",
    ],
  },
  {
    id: "tailles",
    icon: "tree",
    title: "Tailles spécifiques",
    description:
      "Taille en nuage, topiaires et arbres d'exception. Un travail de précision, sujet par sujet.",
    // Bullets reprises telles quelles des titres SPECIALITES (contenu deja valide).
    bullets: ["Taille en nuage Niwaki", "Taille fruitière", "Art topiaire"],
  },
  {
    id: "creation",
    icon: "plant",
    title: "Aménagement extérieur",
    description: "Nous réalisons l'aménagement de vos espaces extérieurs.",
    bullets: ["Terrasses et allées", "Plantations et massifs", "Engazonnement"],
  },
  {
    id: "terrasses",
    icon: "spray",
    title: "Nettoyage de terrasses",
    description:
      "Profiter d'une terrasse propre et éliminer les surfaces glissantes avec un traitement naturel.",
    // Sans puces dans le document client. Pas de lien vers les réalisations
    // non plus : aucun cadre de la galerie ne correspond à cette prestation.
    bullets: [],
  },
] as const;

export type Specialite = {
  id: string;
  icon: "leaf" | "scissors" | "trees" | "bulb";
  title: string;
  description: string;
};

export const SPECIALITES: readonly Specialite[] = [
  {
    id: "niwaki",
    icon: "leaf",
    title: "Taille en nuage Niwaki",
    description:
      "Taille japonaise traditionnelle pour pins, érables et conifères. Forme harmonieuse, longévité de l'arbre.",
  },
  {
    id: "taille-fruitiere",
    icon: "scissors",
    title: "Taille fruitière",
    description: "Optimisation de la production fruitière. Formation et soin de l'arbre.",
  },
  {
    id: "topiaire",
    icon: "trees",
    title: "Art topiaire",
    description: "Sculpture végétale sur buis, ifs et arbustes. Cônes, boules, formes libres.",
  },
] as const;

// Texte de la section Spécialités de /services, fourni par Marion (révision
// du 09/10/2026) et reproduit à la lettre. Un élément = un paragraphe.
// Il remplace les trois cartes à l'affichage ; SPECIALITES reste la source
// des titres de la section et du JSON-LD (lib/seo.ts).
export const SPECIALITES_TEXTE: readonly string[] = [
  "La taille en nuage d'inspiration NIWAKI est un art japonais se rapprochant de celui du bonsaï. Destinée à une intégration dans le paysage, cette pratique vise à reproduire des formes et mouvements de végétaux torturés par les éléments et le temps et que l'on retrouve dans la nature.",
  "Elle sert également à mettre en valeur le caractère des sujets et rendre l'arbre d'apparence plus âgée. En y ajoutant l'aspect très travaillé des plateaux ou nuages, on obtient un contraste saisissant qui sublimera votre jardin.",
  "Vous détenez peut-être dans votre jardin un arbre ou arbuste qui s'y prêterait particulièrement. En effet, il est possible de pratiquer cet art directement sur un sujet en place et avec un peu de patience vous obtiendrez une pièce inestimable. Cela ferait également un joli cadeau pour un proche.",
  "Des spécimens déjà formés sont aussi disponibles en pépinières.",
  "Nous vous proposons de les entretenir et de les conduire pour en exprimer la quintessence.",
] as const;

/**
 * @deprecated REALISATIONS est désormais lu depuis la DB Supabase (table chantiers).
 * Voir lib/queries/chantiers.ts. Conservé uniquement pour le seed (scripts/seed-chantiers.ts)
 * et pour reproduire la liste initiale lors d'une réinitialisation.
 */
export type Realisation = {
  id: string;
  title: string;
  src: string;
  /** Alt descriptif dedie ; a defaut le seed retombe sur `title`. */
  alt?: string;
};

// Fixture du seed (scripts/seed-chantiers.ts). "Tailles spécifiques"
// et "Amenagements exterieurs" portent les photos definitives fournies par le
// client ; entretien reste une PHOTO PLACEHOLDER (illustration existante).
// TODO(client) : remplacer le placeholder entretien par la photo definitive.
export const REALISATIONS: readonly Realisation[] = [
  {
    id: "entretien-du-jardin",
    title: "Entretien du jardin",
    src: "/realisations/entretien-jardin.webp",
  },
  {
    id: "taille-en-nuage-jardin-japonais",
    title: "Taille en nuage dans un jardin d'inspiration japonaise",
    src: "/realisations/taille-en-nuage-jardin-japonais.webp",
    alt: "Taille en nuage d'un conifère dans un jardin d'inspiration japonaise",
  },
  {
    id: "taille-en-nuage-detail",
    title: "Détail des plateaux d'une taille en nuage",
    src: "/realisations/taille-en-nuage-detail.webp",
    alt: "Détail des plateaux d'une taille en nuage réalisée par Symbiosis",
  },
  {
    id: "taille-en-nuage-topiaire",
    title: "Arbre taillé en nuage devant une longère en pierre",
    src: "/realisations/taille-en-nuage-topiaire.webp",
    alt: "Arbre taillé en nuage devant une longère en pierre",
  },
  {
    id: "taille-en-nuage-chantier-en-cours",
    title: "Taille en nuage en cours de réalisation",
    src: "/realisations/taille-en-nuage-chantier-en-cours.webp",
    alt: "Taille en nuage en cours de réalisation, échelle japonaise au premier plan",
  },
  {
    id: "massif-fleuri-amenagement",
    title: "Massif fleuri avec hortensias, iris et bordures acier",
    src: "/realisations/massif-fleuri-amenagement.webp",
    alt: "Massif fleuri avec hortensias, iris et bordures acier",
  },
] as const;

/**
 * Les 3 cadres affichés en page Réalisations, dans l'ordre d'affichage.
 * Chaque cadre regroupe une ou plusieurs catégories DB (`chantiers.categorie`),
 * cf. lib/schemas/chantier.ts. Mapping 7 catégories → 3 cadres : les spécialités
 * de taille (niwaki, taille fruitière, topiaire) tombent sous "Tailles
 * spécifiques" avec l'élagage ; les conseils paysagers sous "Aménagements extérieurs".
 * Le regroupement effectif se fait via lib/realisations.ts.
 */
export type CategorieRealisation = {
  id: "entretien" | "tailles" | "amenagements";
  label: string;
  categories: readonly ChantierCategorie[];
};

export const CATEGORIES_REALISATIONS: readonly CategorieRealisation[] = [
  { id: "entretien", label: "Entretien du jardin", categories: ["entretien"] },
  {
    // L'id "tailles" reste inchange : il sert d'ancre publique
    // (/realisations#tailles), les liens existants restent fonctionnels.
    id: "tailles",
    label: "Tailles spécifiques",
    categories: ["elagage", "niwaki", "taille-fruitiere", "topiaire"],
  },
  { id: "amenagements", label: "Aménagements extérieurs", categories: ["creation", "conseils"] },
] as const;

export const HERO_IMAGE = "/hero.webp";

// Une seule chaîne pour les deux points d'entrée vers /engagements. Écrite en
// dur à deux endroits, elle avait déjà divergé : le footer annonçait « Voir nos
// 6 engagements » et la section Pourquoi « Voir les engagements ».
// Le compte doit suivre la liste ENGAGEMENTS de app/engagements/page.tsx, qui
// en contient bien 6.
export const ENGAGEMENTS_CTA = {
  label: "Voir les 6 engagements",
  href: "/engagements",
} as const;

export type Atout = {
  id: string;
  icon: "bolt" | "certificate" | "leaf" | "message";
  title: string;
  description: string;
  link?: { label: string; href: string };
};

export const POURQUOI: readonly Atout[] = [
  {
    id: "reactifs",
    icon: "bolt",
    title: "Réactifs",
    description: "Prise de contact rapide.",
  },
  {
    id: "engages",
    icon: "leaf",
    title: "Engagés",
    description:
      "Zéro Phyto. Désherbage alternatif, plantations adaptées au climat breton et pratiques raisonnées.",
  },
  {
    id: "transparents",
    icon: "message",
    title: "Transparents",
    description: "Devis détaillé, pas de surprise, facture claire.",
  },
  {
    id: "professionnels",
    icon: "certificate",
    title: "Professionnels",
    description: "Membre UNEP, notre entreprise s'engage.",
    link: ENGAGEMENTS_CTA,
  },
] as const;

export const NAV_LINKS: readonly { label: string; href: string }[] = [
  { label: "Accueil", href: "/" },
  { label: "Présentation", href: "/presentation" },
  { label: "Services", href: "/services" },
  { label: "Réalisations", href: "/realisations" },
  { label: "Contact", href: "/contact" },
] as const;

// Aligné sur NAV_LINKS : le footer sert aussi de plan de site, en sortir
// l'accueil obligeait à passer par le logo pour y revenir.
export const FOOTER_LINKS: readonly { label: string; href: string }[] = [
  { label: "Accueil", href: "/" },
  { label: "Présentation", href: "/presentation" },
  { label: "Services", href: "/services" },
  { label: "Réalisations", href: "/realisations" },
  { label: "Contact", href: "/contact" },
] as const;

export const SITE_URL = "https://symbiosis-paysage.fr";

export type AvisClient = {
  id: string;
  nom: string;
  rating: 5;
  verbatim: string;
  date: string;
  // Format ISO 8601 réservé au schéma SEO (Review.datePublished), pas à l'affichage.
  datePublished: string;
  source: "google";
};

export const AVIS_CLIENTS: readonly AvisClient[] = [
  {
    id: "sylvie-2024-08",
    nom: "Sylvie C-M.",
    rating: 5,
    verbatim:
      "Entretien d'un jardin et taille en nuages de certains arbres. Une équipe sérieuse et à l'écoute. Je recommande.",
    date: "Août 2024",
    datePublished: "2024-08",
    source: "google",
  },
  {
    id: "patricia-2026-02",
    nom: "Patricia P.",
    rating: 5,
    verbatim:
      "Service professionnel, ponctuel, soigneux, respectueux des extérieurs et des intérieurs, aimable, faisant des propositions, échanges charmants et efficaces, bref, c'est parfait !",
    date: "Février 2026",
    datePublished: "2026-02",
    source: "google",
  },
] as const;

export const AVIS_TOTAL_COUNT = 3;
export const AVIS_GMB_URL =
  "https://www.google.com/maps/place/Symbiosis+Paysage+Services/@48.4935679,-1.9556833,15z/data=!4m8!3m7!1s0x64f4e5063eeb662b:0xb57fa249cf4e0fde!8m2!3d48.4935679!4d-1.9556833!9m1!1b1!16s%2Fg%2F11sppgqy6_";

// Chiffres 2026 valides par Marion et Tristan le 2026-08-01 : publiables en
// l'etat. A revalider a chaque loi de finances, le taux comme le plafond
// pouvant bouger d'une annee sur l'autre.
// Le champ `disclaimer` reste affiche en toutes circonstances : il ne signale
// pas un chiffre incertain mais une legislation susceptible d'evoluer. Depuis
// la revision de Marion du 09/10/2026, il ne renvoie plus vers le service des
// impots.
export const CREDIT_IMPOT = {
  rateLabel: "50%",
  ceilingDisplay: "5 000 €",
  maxCreditDisplay: "2 500 €",
  conditions: [
    "Concrètement : 200 € facturés, 100 € de crédit d'impôt",
    "Plafond annuel de 5 000 € de dépenses éligibles pour l'entretien du jardin",
    "Possibilité de ne payer que la moitié de la facture grâce au service Avance immédiate",
  ],
  disclaimer: "Informations susceptibles d'évolution selon la législation en vigueur.",
  ctaLabel: "Renseignez-vous sur les modalités",
} as const;
